import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useDispatch } from 'react-redux';

import { getFormat, makeGraph } from 'lib/commonLib';
import { conversionComparTimeGraph } from 'lib/api/conversion';
import { paramConverter } from 'modules/common';
import { showSuccessSnackbar } from 'modules/snackbar';

import CurrentStatus from 'components/atoms/CurrentStatus';
import InfoTitleArea from 'components/molecules/MbTable/InfoTitleArea';
import ContentsWrap from 'components/molecules/ContentsWrap';
import SwitchingArea from 'components/organisms/SwitchingArea';
import MultiplexChart from 'components/organisms/Chart/MultiplexChart';

const FreqStatisticsCrossGraph = (props) => {
  const { t } = useTranslation();
  const { locale } = useSelector(({ locale }) => ({
    locale: locale.locale,
  }));
  const { params } = props;
  const dispatch = useDispatch();

  // 공통 필터에서 초기값 받아왔는지 확인
  // const [error, setError] = useState(false);
  const [infoMessage, setInfoMessage] = useState(false);
  const [loading, setLoading] = useState(false);
  const initPrimitiveData = {
    weekdayWeb: {},
    weekendWeb: {},
    weekdayMo: {},
    weekendMo: {},
  };
  const [primitiveData, setPrimitiveData] = useState(initPrimitiveData);
  const [curGraphComponent, setCurGraphComponent] = useState([]);

  const graphList = {
    weekdayWeb: {
      title: '온라인 평일',
      data: {
        line: ['WEB_ADVER_ORDER_CNT'],
        bar: ['WEB_ORDER_CNT'],
      },
      yAxisData: {
        left: { WEB_ORDER_CNT: true },
        right: { WEB_ADVER_ORDER_CNT: true },
      },
      customizeData: {
        WEB_ORDER_CNT: {
          stackId: 'WEB_ORDER_CNT',
        },
        WEB_ADVER_ORDER_CNT: {
          stackId: 'WEB_ADVER_ORDER_CNT',
        },
      },
    },
    weekendWeb: {
      title: '온라인 주말',
      data: {
        line: ['WEB_ADVER_ORDER_CNT'],
        bar: ['WEB_ORDER_CNT'],
      },
      yAxisData: {
        left: { WEB_ORDER_CNT: true },
        right: { WEB_ADVER_ORDER_CNT: true },
      },
      customizeData: {
        WEB_ORDER_CNT: {
          stackId: 'WEB_ORDER_CNT',
        },
        WEB_ADVER_ORDER_CNT: {
          stackId: 'WEB_ADVER_ORDER_CNT',
        },
      },
    },
    weekdayMo: {
      title: '모바일 평일',
      data: {
        line: ['MOBILE_ADVER_ORDER_CNT'],
        bar: ['MOBILE_ORDER_CNT'],
      },
      yAxisData: {
        left: { MOBILE_ORDER_CNT: true },
        right: { MOBILE_ADVER_ORDER_CNT: true },
      },
      customizeData: {
        MOBILE_ORDER_CNT: {
          stackId: 'MOBILE_ORDER_CNT',
        },
        MOBILE_ADVER_ORDER_CNT: {
          stackId: 'MOBILE_ADVER_ORDER_CNT',
        },
      },
    },
    weekendMo: {
      title: '모바일 주말',
      data: {
        line: ['MOBILE_ADVER_ORDER_CNT'],
        bar: ['MOBILE_ORDER_CNT'],
      },
      yAxisData: {
        left: { MOBILE_ORDER_CNT: true },
        right: { MOBILE_ADVER_ORDER_CNT: true },
      },
      customizeData: {
        MOBILE_ORDER_CNT: {
          stackId: 'MOBILE_ORDER_CNT',
        },
        MOBILE_ADVER_ORDER_CNT: {
          stackId: 'MOBILE_ADVER_ORDER_CNT',
        },
      },
    },
  };

  const usePrevious = (value) => {
    const ref = useRef();

    useEffect(() => {
      ref.current = value;
    }, [value]);

    return ref.current;
  };
  const prevParams = usePrevious(params);

  useEffect(() => {
    if (JSON.stringify(params) !== JSON.stringify(prevParams)) {
      getData();
    }
    // eslint-disable-next-line
  }, [params]);

  useEffect(() => {
    if (Object.keys(primitiveData).length !== 0) {
      handleChangeGraph();
    }
    // eslint-disable-next-line
  }, [primitiveData]);

  const getData = () => {
    const { sDate, eDate } = params;
    const format = getFormat(locale);

    setInfoMessage(
      t('까지의 통계 그래프입니다.', {
        date: t('term', {
          startDate: moment(sDate).format(format),
          endDate: moment(eDate).format(format),
        }),
      })
    );
    setLoading(true);

    conversionComparTimeGraph({ ...paramConverter(params) })
      .then((response) => {
        const { data } = response.data;
        let weekdayWeb = {};
        let weekdayMo = {};
        let weekendWeb = {};
        let weekendMo = {};
        for (const key in data) {
          for (const device in data[key]) {
            if (device === 'STATS_HH') {
              weekdayWeb[device] = data[key][device];
              weekdayMo[device] = data[key][device];
              weekendWeb[device] = data[key][device];
              weekendMo[device] = data[key][device];
            } else if (device.includes('WEB')) {
              if (key === 'weekday') {
                weekdayWeb[device] = data[key][device];
              } else if (key === 'weekend') {
                weekendWeb[device] = data[key][device];
              }
            } else if (device.includes('MOBILE')) {
              if (key === 'weekday') {
                weekdayMo[device] = data[key][device];
              } else if (key === 'weekend') {
                weekendMo[device] = data[key][device];
              }
            }
          }
        }
        setPrimitiveData({
          weekdayWeb: weekdayWeb,
          weekdayMo: weekdayMo,
          weekendWeb: weekendWeb,
          weekendMo: weekendMo,
        });
      })
      .catch((error) => {
        handleClickSnackbar({
          message: error,
          option: {
            type: 'error',
          },
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleChangeGraph = () => {
    let result = [];
    for (let item in primitiveData) {
      const {
        graphData,
        dataKeys,
        customizeData,
        keyLabel,
        // stackId,
        referenceData,
      } = makeGraph({
        data: graphList[item]['data'],
        customize: graphList[item]['customizeData'],
        primitiveData: primitiveData[item],
        referenceLine: graphList[item]['referenceLine'],
      });
      result.push(
        <div key={item}>
          <MultiplexChart
            width="100%"
            xAxisTick={xAxisTick}
            makeLabel={makeLabel(false)}
            data={graphData}
            dataKeys={dataKeys}
            customizeData={customizeData}
            keyLabel={keyLabel}
            stackId={null}
            yAxisData={graphList[item]['yAxisData']}
            legend
            title={graphList[item]['title']}
            isPercent={false}
            key={graphList[item]['title']}
            referenceLine={referenceData}
            useTitle
          />
        </div>
      );
    }
    setCurGraphComponent(result);
  };

  // 월 일 or 일 반환
  const makeLabel = (onlyDay) => (originalDate) => {
    let color = '';

    return <span style={{ color }}>{originalDate}</span>;
  };

  const xAxisTick = (tickProps) => {
    const { x, y, payload } = tickProps;
    const { value } = payload;

    let fill = '';

    return (
      <text x={x} y={y + 10} fill={fill} textAnchor="middle">
        {value}
      </text>
    );
  };

  /**
   * 스낵바 리덕스 값을 변경해주는 함수
   * @type {(...args: any[]) => any}
   */
  const handleClickSnackbar = useCallback((snackbarOptions) => {
    dispatch(showSuccessSnackbar(snackbarOptions));
    //eslint-disable-next-line
  }, []);

  return (
    <SwitchingArea
      title={
        <InfoTitleArea
          title={t('평일/주말 그래프')}
          tooltipTitle={infoMessage}
          iconType="fainfoCircle"
          iconColor="graph"
        />
      }
      isOpen={false}
    >
      {loading ? (
        <CurrentStatus type="loading" option="useMinHeight" />
      ) : (
        <ContentsWrap contentsCount={2}>{curGraphComponent}</ContentsWrap>
      )}
    </SwitchingArea>
  );
};

export default FreqStatisticsCrossGraph;
